import React from 'react';

/**
 * ONE TYPE SCALE AND ONE FRAME FOR EVERY EXPORTED ASSET.
 *
 * The frames on the export page came from four different places — the home
 * page's marketing board, the product pillars, the AI Agents page — and each
 * of those was composed for its own context. Pulled onto one page and lined
 * up, the mismatch is the first thing you see: a 24px panel title above a
 * 13.5px one, body copy at 14.5px beside body copy at 13px. Individually all
 * defensible; together it reads as assets from three different products.
 *
 * So the scale lives here, once, and every export-only asset uses it. These
 * are deliberately NOT the marketing scale: an asset that will be dropped into
 * a video or a slide is viewed small, so it runs one step tighter than a
 * section on a page.
 *
 *   TITLE   17px / 560  — the object's name
 *   HEADING 19px / 700  — a panel's own header, one step up from a row title
 *   BODY    13px        — supporting line
 *   META    12px        — author, count, timestamp
 *   LABEL   11px caps   — the field name above a value
 *
 * Anything that needs to break this scale is a sign the asset is really two
 * assets.
 */
export const EXPORT_HEADING = 'font-title text-[19px] font-bold leading-tight tracking-[-0.02em] text-[#2B211A]';
export const EXPORT_TITLE = 'font-title text-[17px] font-bold leading-snug tracking-[-0.01em] text-[#2B211A]';
export const EXPORT_BODY = 'text-[13px] leading-relaxed text-[#6B5D52]';
export const EXPORT_META = 'text-[12px] text-[#8A7B6D]';
export const EXPORT_LABEL = 'text-[11px] font-bold uppercase tracking-[0.12em] text-[#A99A8C]';
export const EXPORT_SHADOW = 'shadow-[0_1px_2px_rgba(43,33,26,0.05),0_18px_40px_-18px_rgba(43,33,26,0.22)]';

/**
 * The card every export-only panel sits in — white fill, the warm hairline,
 * 22px radius and the site's two-part shadow. Matches the mosaic board's own
 * card so an export-only asset cannot be told apart from a real one.
 */
export function ExportPanel({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <article className={`relative flex w-full flex-col overflow-hidden rounded-[22px] border border-[#E7E0D4] bg-white ${EXPORT_SHADOW} ${className}`} data-id="element-3223" data-mp="y9hzl948ujeg">
      {children}
    </article>;
}

/**
 * A panel's header. Product UI, not marketing: a field label, the thing's
 * name, and the facts about it — no headline, no sentence of persuasion. The
 * page's copy is what argues; an asset states.
 */
export function PanelHeader({
  label,
  title,
  meta,
  aside
}: {
  label: string;
  title: string;
  meta?: string;
  aside?: React.ReactNode;
}) {
  return <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#EFE7DA] px-6 py-5" data-id="element-3224" data-mp="1bzle1dwvt8tc">
      <div className="min-w-0" data-id="element-3225" data-mp="171a5d6ibp4cp">
        <p className={EXPORT_LABEL} data-id="element-3226" data-mp="17467l717ef6y">{label}</p>
        <h3 className={`${EXPORT_HEADING} mt-1.5`} data-id="element-3227" data-mp="msom9rkcwgg">{title}</h3>
        {meta && <p className={`${EXPORT_META} mt-1.5`} data-id="element-3228" data-mp="3rrj3b3kexkl0">{meta}</p>}
      </div>
      {aside}
    </div>;
}